import type { FC } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { FormCountryAutocomplete } from '@components/organisms/forms';
import { FormAutocomplete } from '@components/organisms/forms/form-autocomplete';
import { FormDatePicker } from '@components/organisms/forms/form-date-picker';
import { FormTextField } from '@components/organisms/forms/form-text-field';
import { FORM_MAX_LENGTH } from '@constants';
import { useGenderOptions } from '@hooks/options';
import { ManualFieldNamesEnum } from '@models/enums';
import { usePersonalData } from '@modules/dashboard/pages/kyc/docs-verification/docs-verification-page/tabs/personal-data/hooks/use-personal-data';
import type { MainFieldProps } from '@modules/dashboard/pages/kyc/docs-verification/docs-verification-page/tabs/personal-data/main-field/types';
import { getManualFieldName } from '@utils/verification/util';
import dayjs from 'dayjs';

export const MainField: FC<MainFieldProps> = ({ field }) => {
    const { data: personalDataFormValues } = usePersonalData();
    const { t } = useTranslation();
    let { label, placeholder } = getManualFieldName(field?.name);
    const genderOptions = useGenderOptions();

    label = t(label);
    placeholder = t(placeholder);

    let maxLength: number;

    switch (field.name) {
        case ManualFieldNamesEnum.PHONE:
            maxLength = FORM_MAX_LENGTH.PHONE_NUMBER;
            break;
        case ManualFieldNamesEnum.SSN:
            maxLength = FORM_MAX_LENGTH.SSN;
            break;
        case ManualFieldNamesEnum.STATE:
            maxLength = FORM_MAX_LENGTH.STATE;
            break;
        case ManualFieldNamesEnum.ZIP:
            maxLength = FORM_MAX_LENGTH.ZIP;
            break;
        default:
            maxLength = FORM_MAX_LENGTH.DEFAULT;
    }

    switch (field.name) {
        case ManualFieldNamesEnum.FULL_NAME:
        case ManualFieldNamesEnum.EMAIL:
        case ManualFieldNamesEnum.PHONE:
        case ManualFieldNamesEnum.STATE:
        case ManualFieldNamesEnum.CITY:
        case ManualFieldNamesEnum.ADDRESS_LINE1:
        case ManualFieldNamesEnum.ADDRESS_LINE2:
        case ManualFieldNamesEnum.SSN:
        case ManualFieldNamesEnum.ZIP:
        case ManualFieldNamesEnum.ADDRESS: {
            return (
                <FormTextField
                    name={field.name}
                    labelProps={{
                        label,
                    }}
                    textFieldProps={{
                        inputProps: { maxLength },
                        fullWidth: true,
                        hint: field.meta?.caption,
                        helperTextStatic: true,
                        placeholder,
                    }}
                />
            );
        }
        case ManualFieldNamesEnum.DOB: {
            return (
                <FormDatePicker
                    name={field.name}
                    labelProps={{
                        label,
                    }}
                    datePickerProps={{
                        disabledDate: (date?: Date) => dayjs(date).isAfter(new Date()),
                        hint: field.meta?.caption,
                        helperTextStatic: true,
                    }}
                />
            );
        }
        case ManualFieldNamesEnum.COUNTRY:
        case ManualFieldNamesEnum.CITIZENSHIP: {
            return (
                <FormCountryAutocomplete
                    name={field.name}
                    autocompleteProps={{
                        defaultValue: personalDataFormValues?.[field.name],
                    }}
                    textFieldProps={{
                        hint: field.meta?.caption,
                        helperTextStatic: true,
                    }}
                    labelProps={{
                        label,
                    }}
                />
            );
        }
        case ManualFieldNamesEnum.GENDER: {
            return (
                <FormAutocomplete
                    name={field.name}
                    options={genderOptions}
                    autocompleteProps={{
                        isOptionEqualToValue: (option, value) => option?.value === value?.value,
                        disableClearable: true,
                        defaultValue: personalDataFormValues?.[field.name],
                    }}
                    textFieldProps={{
                        fullWidth: true,
                        hint: field.meta?.caption,
                        helperTextStatic: true,
                    }}
                    labelProps={{
                        label,
                    }}
                />
            );
        }
    }

    return <React.Fragment />;
};
